import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Search } from 'lucide-react';
import type { EmployeeAdvanceBankAccount } from '@/lib/api/advances';
import { accountingApi } from '@/lib/api/accounting';
import { getBankNameFromCardNumber, findBankIdByCardNumber } from '@/lib/bankCardBin';
import { accountNumberFromSaderatIban } from '@/lib/bsiTransferMode';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** نمایش شماره کارت با فاصله هر ۴ رقم */
function formatCardDisplay(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 16);
  const parts = d.match(/.{1,4}/g) ?? [];
  return parts.join('  ');
}

/** نرمال کردن شبا: IR + ۲۴ رقم (بدون فاصله برای ذخیره) */
function normalizeIban(value: string): string {
  const raw = value.replace(/\s/g, '').toUpperCase();
  const digits = raw.startsWith('IR') ? raw.slice(2).replace(/\D/g, '') : raw.replace(/\D/g, '');
  const num = digits.slice(0, 24);
  return num.length ? 'IR' + num : '';
}

/** نمایش شبا با فاصله هر ۴ رقم طبق فرمول شبا */
function formatIbanDisplay(value: string): string {
  const n = normalizeIban(value);
  if (!n) return value.replace(/\s/g, '').toUpperCase().slice(0, 28);
  if (n.length <= 2) return n;
  const digits = n.slice(2);
  const parts = digits.match(/.{1,4}/g) ?? [];
  return 'IR ' + parts.join(' ');
}

interface BankAccountModalProps {
  show: boolean;
  onClose: () => void;
  employeeId: number;
  editAccount: EmployeeAdvanceBankAccount | null;
  banks: { id: number; name: string }[];
  onSuccess: () => void;
  onError: (message: string) => void;
}

const BankAccountModal: React.FC<BankAccountModalProps> = ({
  show,
  onClose,
  employeeId,
  editAccount,
  banks,
  onSuccess,
  onError,
}) => {
  const [bank_id, setBankId] = useState<number>(0);
  const [account_holder_name, setAccountHolderName] = useState('');
  const [account_number, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');
  const [card_number, setCardNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [is_default, setIsDefault] = useState(false);
  const [is_active, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMsg, setLookupMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const cardInputRef = useRef<HTMLInputElement>(null);
  const lastLookedUpCardRef = useRef('');

  const isEdit = !!editAccount;
  const cardDigits = card_number.replace(/\D/g, '').slice(0, 16);

  useEffect(() => {
    if (!cardInputRef.current || !show) return;
    const formatted = formatCardDisplay(card_number);
    const len = formatted.length;
    cardInputRef.current.setSelectionRange(len, len);
  }, [card_number, show]);

  useEffect(() => {
    if (cardDigits.length < 6 || banks.length === 0) return;
    const matchedBankId = findBankIdByCardNumber(card_number, banks);
    if (matchedBankId != null) setBankId(matchedBankId);
  }, [card_number, banks, cardDigits.length]);

  useEffect(() => {
    if (!show) return;
    setFormError('');
    setLookupMsg(null);
    lastLookedUpCardRef.current = '';
    if (editAccount) {
      setBankId(editAccount.bank_id ?? 0);
      setAccountHolderName(editAccount.account_holder_name ?? '');
      setAccountNumber(editAccount.account_number ?? '');
      setIban(editAccount.iban ?? '');
      setCardNumber(editAccount.card_number ?? '');
      setNotes(editAccount.notes ?? '');
      setIsDefault(!!editAccount.is_default);
      setIsActive(editAccount.is_active !== false);
    } else {
      setBankId(banks[0]?.id ?? 0);
      setAccountHolderName('');
      setAccountNumber('');
      setIban('');
      setCardNumber('');
      setNotes('');
      setIsDefault(false);
      setIsActive(true);
    }
  }, [show, editAccount, banks]);

  const applyLookup = useCallback(
    (data: {
      iban?: string;
      account_holder_name?: string;
      bank_id?: number | null;
      bank_name?: string;
    }) => {
      if (data.iban) {
        const normalized = normalizeIban(data.iban);
        setIban(normalized);
        setAccountNumber((prev) => {
          if (prev.trim()) return prev;
          return accountNumberFromSaderatIban(normalized) || prev;
        });
      }

      if (data.account_holder_name?.trim()) {
        setAccountHolderName(data.account_holder_name.trim());
      }

      if (data.bank_id) {
        setBankId(data.bank_id);
      } else if (data.bank_name && banks.length > 0) {
        const matched = banks.find(
          (b) =>
            b.name.includes(data.bank_name!) ||
            data.bank_name!.includes(b.name) ||
            b.name.replace(/\s/g, '').includes(data.bank_name!.replace(/\s/g, '')),
        );
        if (matched) setBankId(matched.id);
      }
    },
    [banks],
  );

  const runLookup = useCallback(
    async (card: string, opts?: { force?: boolean }) => {
      const digits = card.replace(/\D/g, '').slice(0, 16);
      if (digits.length < 16) {
        setLookupMsg({ type: 'error', text: 'برای استعلام، شماره کارت ۱۶ رقمی وارد کنید.' });
        return;
      }
      if (!opts?.force && lastLookedUpCardRef.current === digits) return;

      setLookupLoading(true);
      setLookupMsg({ type: 'info', text: 'در حال استعلام کارت به شبا...' });
      try {
        const data = await accountingApi.detectBankFromCard(digits);
        lastLookedUpCardRef.current = digits;
        applyLookup({
          iban: data.iban ?? undefined,
          account_holder_name: data.account_holder_name ?? undefined,
          bank_id: data.bank_id ?? undefined,
          bank_name: data.bank_name ?? undefined,
        });
        const bankLabel = data.bank_name ? ` · ${data.bank_name}` : '';
        if (data.iban) {
          setLookupMsg({
            type: 'success',
            text: `استعلام موفق — شبا و مشخصات پر شد${bankLabel}`,
          });
        } else {
          setLookupMsg({
            type: 'error',
            text: `بانک تشخیص داده شد${bankLabel} ولی شبا برنگشت. دوباره تلاش کنید.`,
          });
        }
      } catch (err) {
        setLookupMsg({
          type: 'error',
          text: err instanceof Error ? err.message : 'خطا در استعلام کارت',
        });
      } finally {
        setLookupLoading(false);
      }
    },
    [applyLookup],
  );

  useEffect(() => {
    if (!show || cardDigits.length !== 16) return;
    if (lastLookedUpCardRef.current === cardDigits) return;
    const t = window.setTimeout(() => {
      void runLookup(cardDigits);
    }, 450);
    return () => window.clearTimeout(t);
  }, [show, cardDigits, runLookup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bank_id || !account_holder_name.trim()) {
      const msg = 'بانک و نام صاحب حساب الزامی است.';
      setFormError(msg);
      onError(msg);
      return;
    }
    setLoading(true);
    setFormError('');
    onError('');
    try {
      const payload = {
        bank_id,
        account_holder_name: account_holder_name.trim(),
        account_number: account_number.trim() || undefined,
        iban: normalizeIban(iban) || undefined,
        card_number: cardDigits || undefined,
        notes: notes.trim() || undefined,
        is_default: is_default,
        is_active,
      };
      if (isEdit && editAccount) {
        await accountingApi.updateEmployeeBankAccount(employeeId, editAccount.id, payload);
      } else {
        await accountingApi.createEmployeeBankAccount(employeeId, payload);
      }
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'خطا در ذخیره';
      setFormError(msg);
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  const bankOptions = useMemo(
    () =>
      banks.map((b) => ({
        value: String(b.id),
        label: b.name,
        searchText: b.name,
      })),
    [banks],
  );

  const dialog = (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="z-[220] max-h-[90vh] max-w-md overflow-y-auto [&>button]:left-4 [&>button]:right-auto"
        dir="rtl"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          window.setTimeout(() => cardInputRef.current?.focus(), 0);
        }}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'ویرایش حساب بانکی' : 'افزودن حساب بانکی'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {formError ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {formError}
            </p>
          ) : null}

          <div className="space-y-2">
            <Label>شماره کارت (۱۶ رقم)</Label>
            <div className="flex gap-2">
              <Input
                ref={cardInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={formatCardDisplay(card_number)}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 16);
                  setCardNumber(next);
                  if (next.length < 16) {
                    lastLookedUpCardRef.current = '';
                    setLookupMsg(null);
                  }
                }}
                className="font-mono tracking-widest"
                placeholder="1234  5678  9012  3456"
                dir="ltr"
                maxLength={22}
              />
              <Button
                type="button"
                className="shrink-0 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => void runLookup(cardDigits, { force: true })}
                disabled={lookupLoading || cardDigits.length < 16}
              >
                {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                استعلام
              </Button>
            </div>
            {cardDigits.length >= 6 && getBankNameFromCardNumber(card_number) ? (
              <p className="text-xs text-sky-600">تشخیص اولیه بانک: {getBankNameFromCardNumber(card_number)}</p>
            ) : null}
            {lookupMsg ? (
              <p
                className={`text-xs ${
                  lookupMsg.type === 'success'
                    ? 'text-emerald-700'
                    : lookupMsg.type === 'error'
                      ? 'text-rose-600'
                      : 'text-slate-500'
                }`}
              >
                {lookupMsg.text}
              </p>
            ) : null}
            <p className="text-[11px] text-slate-400">با ۱۶ رقم، بانک و شبا خودکار پر می‌شود.</p>
          </div>

          <div className="space-y-2">
            <Label>بانک *</Label>
            <SearchableSelect
              value={bank_id ? String(bank_id) : ''}
              onChange={(v) => setBankId(v ? parseInt(v, 10) : 0)}
              options={bankOptions}
              placeholder="جستجو و انتخاب بانک..."
              emptyMessage="بانکی یافت نشد"
              popoverClassName="z-[230]"
            />
          </div>

          <div className="space-y-2">
            <Label>نام صاحب حساب *</Label>
            <Input
              value={account_holder_name}
              onChange={(e) => setAccountHolderName(e.target.value)}
              placeholder="نام صاحب حساب"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>شماره شبا (IR + ۲۴ رقم)</Label>
            <Input
              value={formatIbanDisplay(iban)}
              onChange={(e) => setIban(normalizeIban(e.target.value))}
              className="font-mono"
              placeholder="IR 1234 5678 9012 3456 7890 1234"
              dir="ltr"
              maxLength={32}
            />
          </div>

          <div className="space-y-2">
            <Label>شماره حساب</Label>
            <Input
              value={account_number}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="شماره حساب"
              dir="ltr"
            />
          </div>

          <div className="space-y-2">
            <Label>یادداشت</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={is_active}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              />
              کارت / حساب فعال
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={is_default}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              />
              حساب پیش‌فرض
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              انصراف
            </Button>
            <Button type="submit" disabled={loading || lookupLoading}>
              {loading ? 'در حال ذخیره...' : isEdit ? 'ذخیره' : 'افزودن'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(dialog, document.body);
};

export default BankAccountModal;
